import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Autocomplete,
} from '@mui/material';
import { Country, State, City } from 'country-state-city';
import { addNewAddress, editNewAddress } from '../../../Redux/SellerRedux.js';
import { useDispatch } from 'react-redux';

const NewAddress = ({ open, handleClose, addressData }) => {
  const dispatch = useDispatch();

  const [formValues, setFormValues] = useState({
    homeName: '',
    companyName: '',
    doorNumber: '',
    street: '',
    townOrCity: '',
    state: '',
    country: '',
    pincode: '',
    landMark: '',
  });

  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.role || '';

  const countryOptions = Country.getAllCountries().map((country) => ({
    label: country.name,
    value: country.isoCode,
  }));

  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    if (addressData) {
      setFormValues(addressData);
      const selectedCountry = Country.getAllCountries().find(
        (c) => c.name === addressData.country
      );

      if (selectedCountry) {
        setStateOptions(
          State.getStatesOfCountry(selectedCountry.isoCode).map((state) => ({
            label: state.name,
            value: state.isoCode,
          }))
        );
      } else {
        setStateOptions([]);
        setCityOptions([]);
      }
      const selectedState = selectedCountry
        ? State.getStatesOfCountry(selectedCountry.isoCode).find(
            (s) => s.name === addressData.state
          )
        : null;

      if (selectedState) {
        setCityOptions(
          City.getCitiesOfState(
            selectedCountry.isoCode,
            selectedState.isoCode
          ).map((city) => ({
            label: city.name,
            value: city.name,
          }))
        );
      } else {
        setCityOptions([]);
      }
    } else {
      setFormValues({
        homeName: '',
        companyName: '',
        doorNumber: '',
        street: '',
        townOrCity: '',
        state: '',
        country: '',
        pincode: '',
        landMark: '',
      });
      setStateOptions([]);
      setCityOptions([]);
    }
  }, [addressData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (event, value) => {
    if (value) {
      setFormValues((prev) => ({ ...prev, country: value.label }));
      setStateOptions(
        State.getStatesOfCountry(value.value).map((state) => ({
          label: state.name,
          value: state.isoCode,
        }))
      );
      setCityOptions([]);
    } else {
      setFormValues((prev) => ({
        ...prev,
        country: '',
        state: '',
        townOrCity: '',
      }));
      setStateOptions([]);
      setCityOptions([]);
    }
  };

  const handleStateChange = (event, value) => {
    if (value) {
      setFormValues((prev) => ({ ...prev, state: value.label }));
      setCityOptions(
        City.getCitiesOfState(
          countryOptions.find((c) => c.label === formValues.country)?.value,
          value.value
        ).map((city) => ({
          label: city.name,
          value: city.name,
        }))
      );
    } else {
      setFormValues((prev) => ({ ...prev, state: '', townOrCity: '' }));
      setCityOptions([]);
    }
  };

  const handleCityChange = (event, value) => {
    setFormValues((prev) => ({
      ...prev,
      townOrCity: value ? value.label : '',
    }));
  };

  const handleSave = async () => {
    try {
      if (
        !formValues.street ||
        !formValues.townOrCity ||
        !formValues.state ||
        !formValues.country ||
        !formValues.pincode
      ) {
        alert('Please fill all required fields.');
        return;
      }

      const address = { ...formValues };

      if (addressData) {
        await dispatch(editNewAddress(user.userId, address));
      } else {
        await dispatch(addNewAddress(user.userId, address));
      }
      handleClose();
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle className='text-center'>
        {addressData ? 'Edit Address' : 'New Address'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} className='p-3'>
          {userRole !== 'seller' && (
            <Grid className='mt-2' item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Home Name'
                name='homeName'
                value={formValues.homeName}
                onChange={handleChange}
              />
            </Grid>
          )}

          {userRole !== 'buyer' && (
            <Grid className='mt-2' item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Company Name'
                name='companyName'
                value={formValues.companyName}
                onChange={handleChange}
              />
            </Grid>
          )}

          <Grid className='mt-2' item xs={12} sm={6}>
            <Autocomplete
              options={countryOptions}
              getOptionLabel={(option) => option.label}
              onChange={handleCountryChange}
              value={
                countryOptions.find((c) => c.label === formValues.country) ||
                null
              }
              renderInput={(params) => (
                <TextField {...params} label='Country' fullWidth />
              )}
            />
          </Grid>

          <Grid className='mt-2' item xs={12} sm={6}>
            <Autocomplete
              options={stateOptions}
              getOptionLabel={(option) => option.label}
              onChange={handleStateChange}
              value={
                stateOptions.find((s) => s.label === formValues.state) || null
              }
              disabled={!stateOptions.length}
              renderInput={(params) => (
                <TextField {...params} label='State' fullWidth />
              )}
            />
          </Grid>

          <Grid className='mt-2' item xs={12} sm={6}>
            <Autocomplete
              options={cityOptions}
              getOptionLabel={(option) => option.label}
              onChange={handleCityChange}
              value={
                cityOptions.find((c) => c.label === formValues.townOrCity) ||
                null
              }
              disabled={!cityOptions.length}
              renderInput={(params) => (
                <TextField {...params} label='City' fullWidth />
              )}
            />
          </Grid>

          {Object.keys(formValues).map((key) => {
            if (
              [
                'homeName',
                'companyName',
                '_id',
                'isChecked',
                'country',
                'state',
                'townOrCity',
              ].includes(key)
            )
              return null;

            return (
              <Grid className='mt-2' item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={key.replace(/([A-Z])/g, ' $1').trim()}
                  name={key}
                  value={formValues[key]}
                  onChange={handleChange}
                />
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='error' variant='contained'>
          Cancel
        </Button>
        <Button
          onClick={() => handleSave(formValues)}
          color='success'
          variant='contained'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewAddress;
